// pages/reports/ExpenseReportPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Pagination,
} from "react-bootstrap";
import {
  Download,
  RefreshCcw,
  Search,
  ChevronUp,
  ChevronDown,
} from "react-feather";

import * as XLSX from "xlsx";

import { fetchExpenseTypesThunk } from "../../../store/slices/incomeExpenseSlice";
import {
  fetchExpenseReportThunk,
  clearExpenseReport,
} from "../../../store/slices/incomeExpenseReportSlice";


import { printTableReport } from "./reportPrintUtils";

import "./ReportPage.css";

const pad2 = (n) => String(n).padStart(2, "0");

const toIndiaDate = (v) => {
  const s = String(v || "").trim();
  if (!s) return "-";

  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s;

  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;

  const d = new Date(s.includes("T") ? s : s.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return s;

  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
};

const safeAmount = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

export default function ExpenseReportPage() {
  const dispatch = useDispatch();

  const expenseTypes = useSelector(
    (s) => s.incomeExpense?.expenseTypes || []
  );

  const { expenseRows = [], loading, error } = useSelector(
    (s) => s.incomeExpenseReport || {}
  );

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("all");
  const [expenseTypeId, setExpenseTypeId] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [search, setSearch] = useState("");

  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    dispatch(fetchExpenseTypesThunk());
  }, [dispatch]);

  const handleSort = (field) => {
    setSortField((currentField) => {
      if (currentField === field) {
        setSortDirection((currentDirection) =>
          currentDirection === "asc" ? "desc" : "asc"
        );

        return currentField;
      }

      setSortDirection("asc");
      return field;
    });

    setPage(1);
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp size={11} />
          <ChevronDown size={11} />
        </span>
      );
    }

    return sortDirection === "asc" ? (
      <ChevronUp size={14} className="ms-1" />
    ) : (
      <ChevronDown size={14} className="ms-1" />
    );
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    const searchedRows = !q
      ? [...expenseRows]
      : (expenseRows || []).filter((r) => {
          const searchableText = `
            ${r.expense_id}
            ${toIndiaDate(r.date)}
            ${r.expense_type_name}
            ${r.expense_type_id}
            ${r.amount}
            ${r.approval_status}
            ${r.approved}
            ${r.approved_by_name}
            ${r.approved_by}
            ${r.remarks}
          `.toLowerCase();

          return searchableText.includes(q);
        });

    if (!sortField) {
      return searchedRows;
    }

    const numericFields = [
      "expense_id",
      "amount",
    ];

    return [...searchedRows].sort((a, b) => {
      let aValue;
      let bValue;

      if (sortField === "expense_type") {
        aValue = a.expense_type_name || a.expense_type_id || "";
        bValue = b.expense_type_name || b.expense_type_id || "";
      } else if (sortField === "approval_status") {
        aValue =
          a.approval_status ||
          (a.approved ? "approved" : "pending");

        bValue =
          b.approval_status ||
          (b.approved ? "approved" : "pending");
      } else if (sortField === "approved_by") {
        aValue = a.approved_by_name ?? a.approved_by ?? "";
        bValue = b.approved_by_name ?? b.approved_by ?? "";
      } else {
        aValue = a?.[sortField];
        bValue = b?.[sortField];
      }

      if (numericFields.includes(sortField)) {
        aValue = Number(aValue || 0);
        bValue = Number(bValue || 0);

        return sortDirection === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      if (sortField === "date") {
        const aTime = aValue
          ? new Date(
              String(aValue).includes("T")
                ? aValue
                : String(aValue).replace(" ", "T")
            ).getTime()
          : 0;

        const bTime = bValue
          ? new Date(
              String(bValue).includes("T")
                ? bValue
                : String(bValue).replace(" ", "T")
            ).getTime()
          : 0;

        return sortDirection === "asc"
          ? aTime - bTime
          : bTime - aTime;
      }

      const comparison = String(aValue || "").localeCompare(
        String(bValue || ""),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        }
      );

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [expenseRows, search, sortField, sortDirection]);

  useEffect(() => {
    setPage(1);
  }, [
    fromDate,
    toDate,
    status,
    expenseTypeId,
    minAmount,
    maxAmount,
    search,
    expenseRows.length,
    sortField,
    sortDirection,
  ]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const handleLoad = () => {
    dispatch(
      fetchExpenseReportThunk({
        from_date: fromDate || "",
        to_date: toDate || "",
        status: status || "all",
        expense_type_id: expenseTypeId || "",
        min_amount: minAmount || "",
        max_amount: maxAmount || "",
        q: "",
      })
    );
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setStatus("all");
    setExpenseTypeId("");
    setMinAmount("");
    setMaxAmount("");
    setSearch("");
    setSortField("");
    setSortDirection("asc");
    setPage(1);

    dispatch(clearExpenseReport());
  };

  const exportExcel = () => {
    const data = filteredRows.map((r, idx) => ({
      "Sl No.": idx + 1,
      Date: toIndiaDate(r.date),
      "Expense Type":
        r.expense_type_name ||
        r.expense_type_id ||
        "-",
      Amount: safeAmount(r.amount),
      Status:
        r.approval_status ||
        (r.approved ? "approved" : "pending"),
      "Approved By":
        r.approved_by_name ??
        r.approved_by ??
        "-",
      Remarks: r.remarks || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Expense Report"
    );

    XLSX.writeFile(
      wb,
      "expense_report.xlsx"
    );
  };

  const exportPDF = () => {
    const data = filteredRows.map((r, idx) => ({
      "Sl No.": idx + 1,
      Date: toIndiaDate(r.date),
      "Expense Type": r.expense_type_name || r.expense_type_id || "-",
      Amount: `₹ ${safeAmount(r.amount)}`,
      Status: r.approval_status || (r.approved ? "approved" : "pending"),
      "Approved By": r.approved_by_name ?? r.approved_by ?? "-",
      Remarks: r.remarks || "",
    }));

    printTableReport({
      title: "Expense - Report",
fileName: "expense_report.pdf",
      data,
      metaLines: [`Total Records: ${filteredRows.length}`],
    });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxButtons = 5;

    let start = Math.max(
      1,
      currentPage -
        Math.floor(maxButtons / 2)
    );

    let end = Math.min(
      totalPages,
      start + maxButtons - 1
    );

    start = Math.max(
      1,
      end - maxButtons + 1
    );

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() =>
          setPage((p) =>
            Math.max(1, p - 1)
          )
        }
      />
    );

    for (
      let p = start;
      p <= end;
      p++
    ) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={
          currentPage === totalPages
        }
        onClick={() =>
          setPage((p) =>
            Math.min(
              totalPages,
              p + 1
            )
          )
        }
      />
    );

    return (
      <Pagination className="mb-0">
        {items}
      </Pagination>
    );
  };

  const sortableHeader = (
    label,
    field,
    style
  ) => (
    <th style={style}>
      <button
        type="button"
        className={`report-sort-btn ${
          sortField === field
            ? "is-active"
            : ""
        }`}
        onClick={() =>
          handleSort(field)
        }
      >
        {label}
        {renderSortIcon(field)}
      </button>
    </th>
  );

  return (
    <div className="report-page">
      <Card className="report-card">
        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">
                Expense - Report
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error ? (
            <div className="alert alert-danger mb-3">
              {error}
            </div>
          ) : null}

          <div className="report-filter-box">
            <Row className="g-3">
              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  From Date
                </Form.Label>

                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) =>
                    setFromDate(
                      e.target.value
                    )
                  }
                />
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  To Date
                </Form.Label>

                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) =>
                    setToDate(
                      e.target.value
                    )
                  }
                />
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  Status
                </Form.Label>

                <Form.Select
                  value={status}
                  onChange={(e) =>
                    setStatus(
                      e.target.value
                    )
                  }
                >
                  <option value="all">
                    All
                  </option>

                  <option value="pending">
                    Pending
                  </option>

                  <option value="approved">
                    Approved
                  </option>
                </Form.Select>
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  Expense Type
                </Form.Label>

                <Form.Select
                  value={expenseTypeId}
                  onChange={(e) =>
                    setExpenseTypeId(
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    All
                  </option>

                  {expenseTypes.map((x) => (
                    <option
                      key={x.expense_type_id}
                      value={String(
                        x.expense_type_id
                      )}
                    >
                      {x.expense_type_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  Min Amount
                </Form.Label>

                <Form.Control
                  value={minAmount}
                  onChange={(e) =>
                    setMinAmount(
                      e.target.value
                    )
                  }
                  placeholder="0"
                />
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  Max Amount
                </Form.Label>

                <Form.Control
                  value={maxAmount}
                  onChange={(e) =>
                    setMaxAmount(
                      e.target.value
                    )
                  }
                  placeholder="10000"
                />
              </Col>

              <Col
                lg={6}
                md={12}
                className="d-flex align-items-end"
              >
                <div className="report-filter-actions">
                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={handleLoad}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          size="sm"
                          className="me-2"
                        />
                        Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={handleClear}
                  >
                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="report-table-toolbar">
            <div className="report-table-title">
              Expense List{" "}
              <span className="report-count">
                ({total.toLocaleString()} records)
              </span>
            </div>

            <div className="report-table-right">
              <InputGroup className="report-search">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>

                <Form.Control
                  value={search}
                  onChange={(e) =>
                    setSearch(
                      e.target.value
                    )
                  }
                  placeholder="Search type / amount / remarks..."
                />
              </InputGroup>

              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={exportExcel}
                disabled={
                  !filteredRows.length
                }
              >
                <Download
                  size={16}
                  className="me-2"
                />
                Excel
              </Button>

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={exportPDF}
                disabled={
                  !filteredRows.length
                }
              >
                <Download
                  size={16}
                  className="me-2"
                />
                PDF
              </Button>
            </div>
          </div>

          <div className="report-table-wrap">
            <div className="report-table-scroll">
              <Table
                hover
                className="mb-0 report-table"
              >
                <thead>
                  <tr>
                    {sortableHeader(
                      "Sl No.",
                      "expense_id",
                      { width: 100 }
                    )}

                    {sortableHeader(
                      "Date",
                      "date",
                      { width: 120 }
                    )}

                    {sortableHeader(
                      "Expense Type",
                      "expense_type"
                    )}

                    {sortableHeader(
                      "Amount",
                      "amount",
                      { width: 140 }
                    )}

                    {sortableHeader(
                      "Status",
                      "approval_status",
                      { width: 120 }
                    )}

                    {sortableHeader(
                      "Approved By",
                      "approved_by",
                      { width: 160 }
                    )}

                    {sortableHeader(
                      "Remarks",
                      "remarks"
                    )}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-4"
                      >
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Loading...
                      </td>
                    </tr>
                  ) : pagedRows.length ? (
                    pagedRows.map(
                      (r, idx) => (
                        <tr
                          key={String(
                            r.expense_id ||
                              idx
                          )}
                        >
                          <td>
                            {(currentPage - 1) *
                              pageSize +
                              idx +
                              1}
                          </td>

                          <td>
                            {toIndiaDate(
                              r.date
                            )}
                          </td>

                          <td>
                            {r.expense_type_name ||
                              r.expense_type_id ||
                              "-"}
                          </td>

                          <td>
                            ₹ {safeAmount(r.amount)}
                          </td>

                          <td>
                            {r.approval_status ||
                              (r.approved
                                ? "approved"
                                : "pending")}
                          </td>

                          <td>
                            {r.approved_by_name ??
                              r.approved_by ??
                              "-"}
                          </td>

                          <td>
                            {r.remarks || "-"}
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-4"
                      >
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="report-pagination-bar">
              <div className="report-page-size">
                <span>Rows:</span>

                <Form.Select
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);
                    setPageSize(
                      Number(
                        e.target.value
                      )
                    );
                  }}
                  className="page-size-select"
                >
                  {[10, 20, 50, 100].map(
                    (n) => (
                      <option
                        key={n}
                        value={n}
                      >
                        {n}
                      </option>
                    )
                  )}
                </Form.Select>
              </div>

              <div className="report-pagination">
                {renderPagination()}
              </div>

              <div className="report-page-info">
                Page {currentPage} /{" "}
                {totalPages}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
